import { useState, useEffect, useMemo } from 'react';
import { useSettingsQuery } from '@features/setting/queries';
import type { Setting } from '@models/setting';
import { useNavigate } from 'react-router';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  InputAdornment,
  TextField,
  Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { formatRigaDateTime } from '@utils/date';

function SettingsPage() {
  const { data: settings } = useSettingsQuery();
  const [settingsList, setSettingsList] = useState<Setting[]>([]);
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (settings) setSettingsList(settings);
  }, [settings]);

  useEffect(() => {
    const handler = setTimeout(() => setSearch(searchValue), 400);
    return () => clearTimeout(handler);
  }, [searchValue]);

  const filteredSettings = useMemo(() => {
    if (!search) return settingsList;
    return settingsList.filter(
      (setting) =>
        setting.title.toLowerCase().includes(search.toLowerCase()) ||
        (setting.description || '').toLowerCase().includes(search.toLowerCase()) ||
        (setting.slug || '').toLowerCase().includes(search.toLowerCase()),
    );
  }, [settingsList, search]);

  function StatusCircle({ status }: { status: string }) {
    let color;
    switch (status) {
      case 'scheduled':
        color = '#ff9800';
        break;
      case 'active':
        color = '#4caf50';
        break;
      case 'inactive':
        color = '#f44336';
        break;
      default:
        color = '#9e9e9e';
    }
    return (
      <span
        style={{
          display: 'inline-block',
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: color,
          marginRight: 4,
          verticalAlign: 'middle',
        }}
        title={status}
      />
    );
  }

  function renderSettingValueCell(setting: Setting & { isSensitive: boolean }) {
    const value = setting.setting?.value;
    if (setting.isSensitive) {
      return '<hidden>';
    }
    if (typeof value === 'object' && value !== null) {
      return (
        <span
          title={JSON.stringify(value)}
          style={{
            cursor: 'pointer',
            display: 'inline-block',
            maxWidth: 200,
            verticalAlign: 'middle',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {JSON.stringify(value)}
        </span>
      );
    }
    return (
      <span
        title={String(value ?? '')}
        style={{
          cursor: 'pointer',
          display: 'inline-block',
          maxWidth: 200,
          verticalAlign: 'middle',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
        {String(value ?? '')}
      </span>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}>
        <div>
          <h1 style={{ fontSize: 32, fontWeight: 700, margin: 0, color: '#222' }}>Settings</h1>
          <p style={{ color: '#666', marginTop: 8, marginBottom: 0, fontSize: 16 }}>
            Here you can manage your settings and preferences.
          </p>
        </div>
        <TextField
          placeholder="Search settings..."
          size="small"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { borderRadius: 2, bgcolor: '#f5f5f5', minWidth: 220 },
          }}
        />
      </div>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Active From</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSettings.map((setting) => (
              <TableRow key={setting.id}>
                <TableCell>
                  <StatusCircle status={setting.status} />
                </TableCell>
                <TableCell>{setting.title}</TableCell>
                <TableCell>{setting.description}</TableCell>
                <TableCell>{setting.slug}</TableCell>
                <TableCell
                  style={{
                    maxWidth: 220,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {renderSettingValueCell({ ...setting, isSensitive: setting.isSensitive })}
                </TableCell>

                <TableCell>
                  {setting.activeFrom ? (
                    <div>{formatRigaDateTime(setting.activeFrom?.toString())}</div>
                  ) : (
                    ''
                  )}
                </TableCell>

                <TableCell>
                  {setting.updatedAt ? (
                    <div>{formatRigaDateTime(setting.updatedAt?.toString())}</div>
                  ) : (
                    ''
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => navigate(`/dashboard/settings/${setting.id}/edit`)}
                    color="primary"
                    aria-label="edit">
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default SettingsPage;
